import React from "react";
import { Search } from "lucide-react";
import { Button } from "../../atoms/Button/index.js";
import { Checkbox } from "../../atoms/Checkbox/index.js";
import { Input } from "../../atoms/Input/index.js";
import { cn } from "../../lib/utils.js";
import type { RemoteFilterOptions } from "./filter-schema.js";
import { personOption } from "./FilterOptionPerson.js";
import { useRemoteFilterOptions } from "./useRemoteFilterOptions.js";

/* ---------------- FilterOptionList (PB-2941) ----------------
   The remote-source option control inside a FilterMenu field: lazy (mounts
   only while the menu is open, and loading starts on mount), searchable with
   a debounce, cursor-paginated ("Load more"), with truthful loading / empty /
   error / retry states. A stale response can never overwrite a newer search
   (sequence guard + AbortController, the useRemoteOptions pattern), and selected
   values outside the loaded page stay visible as pinned, checked rows.
   CACHING deliberately lives in the module-provided loader (e.g. TanStack
   Query) — this component only drives request timing and UI state. */

export interface FilterOptionListProps {
  readonly source: RemoteFilterOptions;
  /** Accessible name for the option group (the field label). */
  readonly label: string;
  /** `false` = single-select behaviour (a pick replaces the selection). */
  readonly multiple: boolean;
  readonly maxSelected?: number;
  readonly values: readonly string[];
  readonly onChange: (values: readonly string[]) => void;
  /** Display label for a selected value outside the loaded page. */
  readonly selectedLabel?: (value: string) => React.ReactNode | undefined;
}

export function FilterOptionList({
  source,
  label,
  multiple,
  maxSelected,
  values,
  onChange,
  selectedLabel,
}: FilterOptionListProps): React.ReactElement {
  // A single-select field can receive stale or hand-authored URL state with
  // several values. Radios must never render several checked options: retain
  // the first value deterministically until the user replaces or clears it.
  const selectedValues = multiple ? values : values.slice(0, 1);
  const {
    query,
    options,
    nextCursor,
    loading,
    loadingMore,
    failed,
    loadMoreFailed,
    updateQuery,
    loadNextPage,
    retry,
  } = useRemoteFilterOptions(source);

  const toggle = (value: string, checked: boolean): void => {
    if (!multiple) {
      onChange(checked ? [value] : []);
      return;
    }
    onChange(
      checked
        ? [...selectedValues, value]
        : selectedValues.filter((v) => v !== value),
    );
  };

  // Selected values the loaded page does not contain — pinned so a selection
  // (e.g. restored from the URL) is always visible and removable.
  const pinned = selectedValues.filter(
    (value) => !options.some((option) => option.value === value),
  );

  // Single-select renders REAL radios inside a radiogroup. A checkbox
  // announces independent multi-selection, which is exactly what a
  // `type: "single"` field does not offer — a pick REPLACES the selection.
  // The row mirrors the RadioGroup atom's markup (sr-only input + styled dot)
  // rather than mounting that atom, because it owns a fixed option LIST and
  // this one is remote, paged, and pinned; deselection stays the menu's Clear,
  // as it is for radios everywhere.
  const radioName = React.useId();
  const renderOption = (
    value: string,
    optionLabel: React.ReactNode,
    checked: boolean,
    // A person option, and its row's place in the list for its ids.
    person?: { readonly email?: string | null; readonly at: number },
  ): React.ReactElement => {
    const shown = personOption(
      value,
      optionLabel,
      person && { email: person.email, idBase: `${radioName}-p${person.at}` },
    );
    return multiple ? (
      <Checkbox
        key={value}
        {...shown.aria}
        checked={checked}
        disabled={
          !checked &&
          maxSelected !== undefined &&
          selectedValues.length >= maxSelected
        }
        onChange={(next) => toggle(value, next)}
        label={shown.label}
      />
    ) : (
      <label
        key={value}
        className="inline-flex cursor-pointer select-none items-center gap-2"
      >
        <span className="relative inline-flex">
          <input
            type="radio"
            name={radioName}
            {...shown.aria}
            checked={checked}
            onChange={() => toggle(value, true)}
            className="peer sr-only"
          />
          <span
            className={cn(
              "flex h-4 w-4 items-center justify-center rounded-full border transition-colors",
              checked
                ? "border-pb-teal bg-pb-card"
                : "border-pb-control-border bg-pb-card",
              "peer-focus-visible:ring-2 peer-focus-visible:ring-pb-teal/40",
            )}
          >
            {checked ? (
              <span className="h-2 w-2 rounded-full bg-pb-teal" />
            ) : null}
          </span>
        </span>
        <span className="text-sm text-pb-text">{shown.label}</span>
      </label>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      {source.searchable ? (
        <Input
          type="search"
          aria-label={`Search ${label} options`}
          placeholder={source.searchPlaceholder ?? "Search…"}
          value={query}
          onChange={(event) => updateQuery(event.target.value)}
          prefix={<Search className="h-3.5 w-3.5" />}
        />
      ) : null}
      <div
        role={multiple ? "group" : "radiogroup"}
        aria-label={`${label} options`}
        aria-busy={loading || loadingMore}
        className="flex max-h-56 flex-col gap-1.5 overflow-y-auto"
      >
        {pinned.map((value) =>
          renderOption(value, selectedLabel?.(value) ?? value, true),
        )}
        {loading ? (
          <p role="status" className="px-0.5 py-1 text-xs text-pb-text-muted">
            Loading options…
          </p>
        ) : failed ? (
          <div className="flex items-center gap-2 px-0.5 py-1">
            <span role="status" className="text-xs text-pb-red-text">
              Options could not be loaded.
            </span>
            <Button variant="ghost" size="sm" onClick={retry}>
              Retry
            </Button>
          </div>
        ) : options.length === 0 && pinned.length === 0 ? (
          <p role="status" className="px-0.5 py-1 text-xs text-pb-text-muted">
            {source.emptyMessage ?? "No options found."}
          </p>
        ) : (
          options.map((option, at) =>
            renderOption(
              option.value,
              option.label,
              selectedValues.includes(option.value),
              option.person ? { ...option.person, at } : undefined,
            ),
          )
        )}
        {!loading && !failed && nextCursor ? (
          /* The load-more error sits BESIDE the control that caused it and
             leaves the loaded options in place. Rendering it where the
             first-page error goes would clear the list — including checked rows
             the reader can see — to report that one more page did not arrive. */
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={loadNextPage}
              loading={loadingMore}
              className="self-start"
            >
              {loadMoreFailed ? "Try again" : "Load more"}
            </Button>
            {loadMoreFailed ? (
              <span role="status" className="text-xs text-pb-red-text">
                More options could not be loaded.
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      {maxSelected !== undefined && selectedValues.length >= maxSelected && (
        <p className="text-xs text-pb-text-muted">
          You can select up to {maxSelected} {label}.
        </p>
      )}
    </div>
  );
}
